<template>
  <div class="datepicker" ref="rootEl">
    <template v-if="hasInput">
      <input
        type="text"
        class="form-control datepicker-input"
        ref="refDatepickerInput"
        :class="inputClassObjComputed"
        :placeholder="placeholder"
        :style="{'background-color': dateInputBgColor}"
        :readonly="readonly"
        @click="inputClick"
        @input="onInput"
        @keydown="onInputKeydown"
        @keyup="onInputKeyup"
        v-model="state.inputValue"
      />
      <button v-if="inputClearButton && modelValue" type="button" class="close" @click="clear(); close()">
        <span>&times;</span>
      </button>
    </template>
    <div class="datepicker-popup" :style="state.paneStyle" v-show="state.view === 'day'">
      <div class="datepicker-ctrl">
        <span class="datepicker-preBtn fas fa-chevron-left" aria-hidden="true" @click="preNextMonthClick(0)"></span>
        <span class="datepicker-nextBtn fas fa-chevron-right" aria-hidden="true" @click="preNextMonthClick(1)"></span>
      </div>
      <div class="clearfix">
        <div class="datepicker-inner" v-for="(p, pan) in pane" :key="pan">
          <div class="datepicker-body">
            <p class="ym-disp1" @click="switchMonthView">{{stringifyDayHeader(state.currDate, pan)}}</p>
            <div class="datepicker-weekRange">
              <span v-for="(w, i) in text.daysOfWeek" :key="i">{{w}}</span>
            </div>
            <div class="datepicker-dateRange">
              <span
                v-for="(d, di) in state.dateRange[pan]"
                :key="di"
                class="day-cell"
                :class="getItemClasses(d)"
                :data-date="stringify(d.date)"
                @click="daySelect(d.date, $event)"
              >
                <div>
                  <template v-if="d.sclass !== 'datepicker-item-gray'">
                    {{getSpecialDay(d.date) || d.text}}
                  </template>
                  <template v-else>
                    {{d.text}}
                  </template>
                  <div v-if="d.sclass !== 'datepicker-item-gray'"><slot :name="stringify(d.date)" /></div>
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="datepicker-footer" v-if="displayDatePickerFooter">
        <div class="btn-bar">
          <button
            class="btn btn-sm btn-primary today" type="button"
            @click="setToday(); close(); focusInput()"
          >{{text.today}}</button>
          <button
            type="button"
            class="btn btn-sm btn-primary clear"
            :class="{
              'btn-primary': !clearButton2Disabled,
              'btn-default': clearButton2Disabled,
            }"
            :disabled="clearButton2Disabled"
            @click="clear(); close(); focusInput()"
          >{{text.clear}}</button>
          <button type="button" class="btn btn-sm btn-secondary close2" @click="close(); focusInput()">
            {{text.close}}
          </button>
        </div>
      </div>
    </div>
    <div class="datepicker-popup" :style="state.paneStyle" v-show="state.view === 'month'">
      <div class="datepicker-ctrl">
        <span class="datepicker-preBtn fas fa-chevron-left" aria-hidden="true" @click="preNextYearClick(0)"></span>
        <span class="datepicker-nextBtn fas fa-chevron-right" aria-hidden="true" @click="preNextYearClick(1)"></span>
      </div>
      <template v-for="(p, pan) in pane" :key="pan">
        <div class="datepicker-inner">
          <div class="datepicker-body">
            <p @click="switchDecadeView">{{getYearHeaderValue(state.currDate, pan)}}</p>
            <div class="datepicker-monthRange">
              <template v-for="(m, $index) in text.months" :key="$index">
                <span :class="{'datepicker-dateRange-item-active':
                    (text.months[parseDateWithDefault(modelValue ?? '').getMonth()]  === m) &&
                    state.currDate.getFullYear() + pan === parseDateWithDefault(modelValue ?? '').getFullYear()}"
                    @click="monthSelect(getYearHeaderValue(state.currDate, pan), $index)"
                  >{{m.substr(0,3)}}</span>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="datepicker-popup" :style="state.paneStyle" v-show="state.view === 'year'">
      <div class="datepicker-ctrl">
        <span class="datepicker-preBtn fas fa-chevron-left" aria-hidden="true" @click="preNextDecadeClick(0)"></span>
        <span class="datepicker-nextBtn fas fa-chevron-right" aria-hidden="true" @click="preNextDecadeClick(1)"></span>
      </div>
      <template v-for="(p, pan) in pane" :key="pan">
        <div class="datepicker-inner">
          <div class="datepicker-body">
            <p>{{stringifyDecadeHeader(state.currDate, pan)}}</p>
            <div class="datepicker-monthRange decadeRange">
              <template v-for="(decade, decadeIdx) in state.decadeRange[pan]" :key="decadeIdx">
                <span
                  :class="{'datepicker-dateRange-item-active':
                    parseDateWithDefault(state.inputValue).getFullYear() === decade.text
                  }"
                  @click.stop="yearSelect(decade.text)"
                >{{decade.text}}</span>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { calendarLangFunc, type CalendarLang } from 'src/consts/calendar';
import {
  parse,
  detectDateEmitFormat,
  stringifyDecadeHeader,
  getYearHeaderValue,
} from 'src/lib/calendarHelper';
import { dtFormat } from 'src/lib/dateHelper';
import type { CalendarProps, CalendarEmit, CalendarState } from 'src/models/calendar';
import { useCalendarPane } from 'src/components/lib/Calendar/useCalendarPane';

const props = withDefaults(defineProps<CalendarProps>(), {
  format: 'mm/dd/yyyy',
  inputClassObj: () => ({}),
  disabledDaysOfWeek: () => [],
  inputClearButton: false,
  clearButton2Disabled: false,
  readonly: false,
  lang: navigator.language,
  hasInput: true,
  pane: 1,
  borderWidth: 2,
  closeCalendarOnEnter: true,
  onDayClick: () => {},
  changePane: () => {},
  specialDays: () => ({}),
  disableSwitchMonthView: false,
  dayHeaderFormat: 'm Y',
  displayDatePickerFooter: true,
  dateInputBgColor: 'white',
});

const emit = defineEmits<CalendarEmit>();

const modelValue = defineModel<string>();

const stringify = (date: Date, format?: string): string =>
  dtFormat(date, format ?? state.dateFormat);

const rawValueToInputValue = (val: string, format: string): string => {
  const parsedDate = parse(val, null);
  if (!parsedDate) return val;

  return stringify(parsedDate, format);
};

const state = reactive<CalendarState>({
  inputValue: rawValueToInputValue(modelValue.value ?? '', props.format),
  dateFormat: props.format,
  dateEmitFormat: detectDateEmitFormat(props.format),
  currDate: new Date(),
  dateRange: [],
  decadeRange: [],
  paneStyle: {
    width: '',
    right: '',
    position: 'absolute',
  },
  view: 'none',
});

const inputClassObjComputed = computed<Record<string, string>>(() => ({
  ...props.inputClassObj,
  'with-reset-button': props.inputClearButton ? 'with-reset-button' : '',
}));

const emitValue = computed<string>(() => {
  const parsedDate = parse(state.inputValue, null);
  if (!parsedDate) return state.inputValue;

  return stringify(parsedDate, state.dateEmitFormat);
});

const onInput = (): void => {
  modelValue.value = emitValue.value;
};

const clear = (): void => {
  state.inputValue = '';
  onInput();
};

const refDatepickerInput = ref<HTMLInputElement>();

const focusInput = (): void => {
  if (props.hasInput) {
    setTimeout(() => refDatepickerInput.value?.focus(), 0);
  }
};

const {
  rootEl,
  close,
  inputClick,
  switchMonthView,
  switchDecadeView,
  yearSelect,
  monthSelect,
  daySelect,
  preNextDecadeClick,
  preNextMonthClick,
  preNextYearClick,
  getItemClasses,
  getSpecialDay,
  stringifyDayHeader,
  setToday,
  parseDateWithDefault,
} = useCalendarPane(props, state, { stringify, onInput });

watch(modelValue, (val) => {
  state.inputValue = rawValueToInputValue(val ?? '', state.dateFormat);
  state.dateEmitFormat = detectDateEmitFormat(val ?? '');
});

const onInputKeydown = ($event: KeyboardEvent): void => {
  if ($event.key === 'Tab') close();
  if ($event.code === 'Space') {
    inputClick();
    $event.stopImmediatePropagation();
    $event.preventDefault();
  }
};

const onInputKeyup = ($event: KeyboardEvent): void => {
  if (props.closeCalendarOnEnter && $event.key === 'Enter') close();
  emit('keyup', $event);
};

const text = computed<CalendarLang>(() => calendarLangFunc(props.lang));
</script>

<style lang="scss" scoped>
@mixin clearfix() {
  &:before,
  &:after {
    content: " "; // 1
    display: table; // 2
  }
  &:after {
    clear: both;
  }
}
.datepicker{
  position: relative;
  display: inline-block;
}
.datepicker-input {
  &.with-reset-button {
    padding-right: 20px;
  }
  padding: 0.375rem 0.75rem;
}
.datepicker > button.close {
  position: absolute;
  top: 0;
  right: 0;
  outline: none;
  z-index: 2;
  display: block;
  width: 24px;
  height: 34px;
  line-height: 34px;
  text-align: center;
}
.datepicker > button.close:focus {
  opacity: .2;
}
.datepicker-popup {
  position: absolute;
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #fff;
  margin-top: 2px;
  z-index: 1000;
  box-shadow: 0 6px 12px rgba(0,0,0,0.175);
  font-family: sans-serif;
  white-space: normal;
  @include clearfix;
}
.datepicker-inner{
  width: 218px;
  float: left;
}
.datepicker-body{
  padding: 10px 10px;
  text-align: center;
}
.datepicker-ctrl span,
.datepicker-body span{
  display: inline-block;
  width: 28px;
  line-height: 28px;
  height: 28px;
}
.datepicker-ctrl span {
  position: absolute;
}
.datepicker-body span {
  text-align: center;
  &.today {
    background-color: rgb(216, 237, 255);
    &:hover {
      background-color: rgb(201, 215, 226);
    }
  }
}
.datepicker-monthRange span{
  width: 48px;
  height: 50px;
  line-height: 45px;
}
.datepicker-item-disable {
  background-color: white!important;
  cursor: not-allowed!important;
}
.decadeRange span:first-child,
.decadeRange span:last-child,
.datepicker-item-disable,
.datepicker-item-gray{
  color: #999;
}
.datepicker-dateRange-item-active:hover,
.datepicker-dateRange-item-active {
  background: rgb(50, 118, 177)!important;
  color: white!important;
}
.datepicker-monthRange {
  margin-top: 10px
}
.datepicker-monthRange span,
.datepicker-ctrl span,
.datepicker-dateRange span {
  cursor: pointer;
}
.datepicker-monthRange span:hover,
.datepicker-dateRange span:hover {
  background-color : #eeeeee;
}
.datepicker-weekRange span{
  font-weight: bold;
}
.datepicker-ctrl{
  position: relative;
  line-height: 30px;
  font-weight: bold;
  text-align: center;
}
.datepicker-preBtn{
  top: 3px;
  left: 2px;
}
.datepicker-nextBtn{
  top: 3px;
  right: 2px;
}

.ym-disp1 {
  font-size: 16px;
  margin: -4px 0 10px;
}
.datepicker-footer {
  .btn-bar {
    display: flex;
    justify-content: space-around;
    margin: 0 0 6px;
  }
}
</style>
